"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";

const QueryFormWidget = dynamic(() => import("./QueryFormWidget"), { ssr: false });

// Routes where the right-side query widget must not appear: it auto-opens on
// a first visit, so on the authentication pages it popped open over the
// login / sign-up / reset / OTP forms. (Only /register was covered before.)
const HIDDEN_ON_PATHS = new Set([
  "/login",
  "/register",
  "/registerhere",
  "/registerverification",
  "/forgotpassword",
  "/phone-number",
]);

function normalizePath(pathname) {
  if (!pathname) return "";
  return (pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname).toLowerCase();
}

function isHiddenPath(pathname) {
  return HIDDEN_ON_PATHS.has(normalizePath(pathname));
}

// Defers mounting the widget (and its code-split chunk) until the browser is
// idle, so it never competes with the page's own critical rendering work.
export default function QueryFormWidgetLoader() {
  const pathname = usePathname();
  const hidden = isHiddenPath(pathname);
  // On the home page the widget opens by itself, so it mounts right away
  // instead of waiting for idle time.
  const isHome = normalizePath(pathname) === "/";
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (hidden || isHome) return;
    if ("requestIdleCallback" in window) {
      const id = requestIdleCallback(() => setShouldLoad(true), { timeout: 5000 });
      return () => cancelIdleCallback(id);
    }
    const timer = setTimeout(() => setShouldLoad(true), 4000);
    return () => clearTimeout(timer);
  }, [hidden, isHome]);

  if (hidden || !(isHome || shouldLoad)) return null;
  return <QueryFormWidget autoOpen={isHome} />;
}
